import * as React from "react";

export type RootErrorBoundaryVariant = "bag" | "success";

export interface RootErrorBoundaryProps {
    variant: RootErrorBoundaryVariant;
    cartUrl?: string;
    homeUrl?: string;
    onReport?: (error: Error, info: React.ErrorInfo) => void;
    children?: React.ReactNode;
}

const defaultReport = (error: Error, info: React.ErrorInfo): void => {
    try {
        console.error(error, info);
    } catch {
        // A console failure must not prevent the optional telemetry report.
    }

    try {
        const monitoringWindow = window as Window & {
            NREUM?: { noticeError?: (error: Error) => void };
        };
        monitoringWindow.NREUM?.noticeError?.(error);
    } catch {
        // Telemetry failures must not prevent the fallback from rendering.
    }
};

export class RootErrorBoundary extends React.Component<
    RootErrorBoundaryProps,
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError(): { hasError: boolean } {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        try {
            (this.props.onReport ?? defaultReport)(error, info);
        } catch {
            // A custom reporter must not take down the fallback.
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children ?? null;
        }

        const {
            variant,
            cartUrl = "/checkout/cart/",
            homeUrl = "/",
        } = this.props;
        let message = "Sorry, something went wrong.";
        let cartLinkText = "Go to your bag";
        let homeLinkText = "Continue shopping";
        try {
            const magentoWindow = window as Window & {
                jQuery?: { mage?: { __?: (message: string) => string } };
            };
            if (typeof magentoWindow.jQuery?.mage?.__ === "function") {
                const translatedMessage =
                    magentoWindow.jQuery.mage.__("Sorry, something went wrong.") ??
                    message;
                const translatedCartLinkText =
                    magentoWindow.jQuery.mage.__("Go to your bag") ?? cartLinkText;
                const translatedHomeLinkText =
                    magentoWindow.jQuery.mage.__("Continue shopping") ??
                    homeLinkText;
                message = translatedMessage;
                cartLinkText = translatedCartLinkText;
                homeLinkText = translatedHomeLinkText;
            }
        } catch {
            // Keep the English labels if translation lookup or any call fails.
        }

        return (
            <div role="alert">
                <p>{message}</p>
                {variant === "bag" && (
                    <React.Fragment>
                        <a href={cartUrl}>{cartLinkText}</a>{" "}
                    </React.Fragment>
                )}
                <a href={homeUrl}>{homeLinkText}</a>
            </div>
        );
    }
}
